<template>
    <component
        :is="to ? 'router-link' : href ? 'a' : 'button'"
        class="ds-button transition-[height,_padding,_font-size,_gap]"
        :class="[
            `ds-${variant}`,
            `ds-size-${btnSize}`,
            contextClass,
            {
                'ds-loading': loading,
                'ds-square': iconOnly,
            },
        ]"
        v-bind="to ? { to } : { href, target }"
        :disabled="!to && !href ? disabled || loading : undefined"
        :download
        :type="!to && !href ? type : undefined"
    >
        <slot name="leading">
            <DsIcon
                v-if="leading"
                :icon="leading"
                :size="iconSize"
            />
        </slot>

        <div class="relative">
            <slot>
                <!-- Button text will appear here -->
            </slot>
            <DsLoadingSpinner
                v-if="loading"
                class="loading-spinner"
                :size="iconSize"
            />
        </div>

        <slot name="trailing">
            <DsIcon
                v-if="trailing"
                :icon="trailing"
                :size="iconSize"
            />
        </slot>
    </component>
</template>

<script lang="ts" setup>
import type { BreakpointSize } from '@/components/design-system/design-system';
import { getLargestBreakpointMatch } from '@ds/design-system-breakpoints';
import type { DsIconProps } from '@ds/DsIcon.vue';
import DsIcon from '@ds/DsIcon.vue';
import DsLoadingSpinner from '@ds/spinner/DsSpinner.vue';
import type { FontAwesomeIconProps } from '@fortawesome/vue-fontawesome';
import { computed } from 'vue';
import type { RouteLocationRaw } from 'vue-router';

type ButtonSize = 'lg' | 'md' | 'sm' | 'xs';

export interface DsButtonProps {
    destructive?: boolean;
    disabled?: boolean;
    href?: HTMLAnchorElement['href'] | null;
    to?: RouteLocationRaw;
    iconOnly?: boolean;
    leading?: FontAwesomeIconProps['icon'];
    loading?: boolean;
    onDarkBg?: boolean;
    rel?: HTMLAnchorElement['rel'];
    size: ButtonSize | Partial<Record<BreakpointSize, ButtonSize>>;
    target?: HTMLAnchorElement['target'];
    trailing?: FontAwesomeIconProps['icon'];
    type?: HTMLInputElement['type'];
    variant: 'primary' | 'secondary' | 'tertiary' | 'icon' | 'link-button';
    download?: string;
}

const props = withDefaults(defineProps<DsButtonProps>(), {
    type: 'button',
});

const btnSize = computed<ButtonSize>(() => {
    if (typeof props.size === 'string') return props.size;
    return getLargestBreakpointMatch(props.size) ?? 'md';
});

const ICON_SIZE_BY_BUTTON_SIZE = {
    lg: 'md',
    md: 'md',
    sm: 'sm',
    xs: 'xs',
} as const satisfies Record<ButtonSize, DsIconProps['size']>;

const iconSize = computed(() => ICON_SIZE_BY_BUTTON_SIZE[btnSize.value]);

const contextClass = computed(() => {
    if (props.destructive) return 'ds-context-destructive';
    if (props.onDarkBg) return 'ds-context-on-dark';
    return 'ds-context-default';
});
</script>

<style scoped lang="scss">
.ds-button {
    @apply inline-flex min-w-max items-center justify-center rounded-lg;
    transition: background-color 200ms;

    &:focus-visible {
        @apply outline-none ring-4 ring-ds-primary-300;
    }

    &:disabled {
        @apply cursor-not-allowed;
    }

    &.ds-loading {
        @apply cursor-progress;
    }
}

.ds-primary {
    &.ds-context-default {
        @apply bg-semantic-primary-default text-content-inverse;

        &:hover {
            @apply bg-semantic-primary-hover;
        }

        &:active {
            @apply bg-semantic-primary-pressed;
        }

        &:disabled {
            @apply bg-ds-primary-200 text-content-disabled;

            &.ds-loading {
                @apply bg-semantic-primary-pressed text-transparent;

                .loading-spinner {
                    @apply text-content-inverse;
                }
            }
        }
    }

    &.ds-context-on-dark {
        @apply bg-semantic-inverted-default text-content-primary;

        &:hover {
            @apply bg-semantic-inverted-hover;
        }

        &:active {
            @apply bg-semantic-inverted-pressed;
        }

        &:disabled {
            @apply bg-ds-primary-300 text-content-disabled;

            &.ds-loading {
                @apply bg-semantic-primary-pressed text-transparent;

                .loading-spinner {
                    @apply text-content-inverse;
                }
            }
        }
    }

    &.ds-context-destructive {
        @apply bg-semantic-destructive-primary-default text-content-inverse;

        &:hover {
            @apply bg-semantic-destructive-primary-hover;
        }

        &:active {
            @apply bg-semantic-destructive-primary-pressed;
        }

        &:disabled {
            @apply border-ds-red-200 bg-ds-red-200 text-content-disabled;

            &.ds-loading {
                @apply bg-semantic-destructive-primary-pressed text-transparent;

                .loading-spinner {
                    @apply text-content-inverse;
                }
            }
        }
    }
}

.ds-secondary {
    &.ds-context-default {
        @apply border border-ds-primary-500 bg-fill-primary text-content-primary;

        &:hover {
            @apply bg-semantic-secondary-hover;
        }

        &:active {
            @apply bg-semantic-secondary-pressed;
        }

        &:disabled {
            @apply border-ds-primary-300 bg-fill-primary text-content-disabled;

            &.ds-loading {
                @apply border-ds-primary-500 bg-semantic-secondary-pressed text-transparent;

                .loading-spinner {
                    @apply text-content-primary;
                }
            }
        }
    }

    &.ds-context-on-dark {
        @apply border border-white bg-transparent text-content-inverse;

        &:hover {
            @apply bg-semantic-on-dark-hover;
        }

        &:active {
            @apply bg-semantic-on-dark-pressed;
        }

        &:disabled {
            @apply border-ds-primary-500 bg-transparent text-content-disabled;

            &.ds-loading {
                @apply border-none bg-semantic-on-dark-pressed text-transparent;

                .loading-spinner {
                    @apply text-content-inverse;
                }
            }
        }
    }

    &.ds-context-destructive {
        @apply border border-semantic-destructive-primary-default bg-fill-primary text-semantic-destructive-primary-default;

        &:hover {
            @apply bg-semantic-destructive-secondary-hover;
        }

        &:active {
            @apply bg-semantic-destructive-secondary-pressed;
        }

        &:disabled {
            @apply border-ds-red-400 bg-fill-primary text-ds-red-400;

            &.ds-loading {
                @apply border-none bg-semantic-destructive-secondary-pressed text-transparent;

                .loading-spinner {
                    @apply text-ds-red-500;
                }
            }
        }
    }
}

.ds-tertiary {
    @apply underline;

    &.ds-context-default {
        @apply bg-fill-primary text-content-primary;

        &:hover {
            @apply bg-semantic-secondary-hover;
        }

        &:active {
            @apply bg-semantic-secondary-pressed;
        }

        &:disabled {
            @apply bg-fill-primary text-content-disabled;

            &.ds-loading {
                @apply bg-semantic-secondary-pressed text-transparent;

                .loading-spinner {
                    @apply text-content-primary;
                }
            }
        }
    }

    &.ds-context-on-dark {
        @apply bg-transparent text-content-inverse;

        &:hover,
        &:focus-visible {
            @apply bg-semantic-on-dark-hover;
        }

        &:active {
            @apply bg-semantic-on-dark-pressed;
        }

        &:disabled {
            @apply bg-transparent text-ds-primary-500;

            &.ds-loading {
                @apply bg-semantic-on-dark-pressed text-transparent;

                .loading-spinner {
                    @apply text-content-inverse;
                }
            }
        }
    }

    &.ds-context-destructive {
        @apply text-semantic-destructive-primary-default;

        &:hover {
            @apply bg-semantic-destructive-secondary-hover;
        }

        &:active {
            @apply bg-semantic-destructive-secondary-pressed;
        }

        &:disabled {
            @apply border-ds-red-400 bg-fill-primary text-ds-red-400;

            &.ds-loading {
                @apply bg-semantic-destructive-secondary-pressed text-transparent;

                .loading-spinner {
                    @apply text-ds-red-500;
                }
            }
        }
    }
}

.ds-icon {
    &.ds-context-default {
        @apply bg-transparent;

        &:hover {
            @apply bg-semantic-secondary-hover;
        }

        &:active {
            @apply bg-semantic-secondary-pressed;
        }

        &:disabled {
            @apply bg-transparent text-content-disabled;

            &.ds-loading {
                @apply text-transparent;

                .loading-spinner {
                    @apply text-semantic-primary-default;
                }
            }
        }
    }

    &.ds-context-on-dark {
        @apply text-content-inverse;

        &:hover {
            @apply bg-semantic-on-dark-hover;
        }

        &:active {
            @apply bg-semantic-on-dark-pressed;
        }

        &:disabled {
            @apply bg-transparent text-content-disabled;

            &.ds-loading {
                @apply text-transparent;

                .loading-spinner {
                    @apply text-content-inverse;
                }
            }
        }
    }
}

.ds-link-button {
    &.ds-size-lg,
    &.ds-size-md,
    &.ds-size-sm {
        @apply h-auto px-0;
    }

    &.ds-context-default {
        @apply bg-transparent text-semantic-primary-default underline;

        &:hover {
            @apply text-semantic-primary-hover no-underline;
        }

        &:active {
            @apply text-semantic-primary-pressed;
        }

        &:disabled {
            @apply text-content-disabled underline;

            &.ds-loading {
                @apply text-transparent;

                .loading-spinner {
                    @apply text-semantic-primary-default;
                }
            }
        }
    }

    &.ds-context-on-dark {
        @apply text-content-inverse underline;

        &:hover {
            @apply text-semantic-on-dark-hover no-underline;
        }

        &:active {
            @apply text-semantic-on-dark-pressed;
        }

        &:disabled {
            @apply text-content-disabled underline;

            &.ds-loading {
                @apply text-transparent;

                .loading-spinner {
                    @apply text-content-inverse;
                }
            }
        }
    }

    &.ds-context-destructive {
        @apply text-semantic-destructive-primary-default underline;

        &:hover {
            @apply text-semantic-destructive-secondary-hover no-underline;
        }

        &:active {
            @apply text-semantic-destructive-secondary-pressed;
        }

        &:disabled {
            @apply text-ds-red-400 underline;

            &.ds-loading {
                @apply text-transparent;

                .loading-spinner {
                    @apply text-ds-red-400;
                }
            }
        }
    }
}

.ds-size-lg {
    @apply h-12 gap-2 px-6 text-content-lg;
}

.ds-size-md {
    @apply h-10 gap-2 px-4 text-content-lg;
}

.ds-size-sm {
    @apply h-8 gap-1 px-2 text-content-md;
}

.ds-size-xs {
    @apply h-6 gap-1 px-2 text-content-sm;
}

.ds-square {
    @apply aspect-square items-center justify-center p-0;
}

.loading-spinner {
    @apply absolute inset-0 flex items-center justify-center;
}
</style>
